import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * URL-synced nested board stack.
 *
 * The root boardId is implicit (always at index 0). Descendants are stored in the
 * `?board=<id1>.<id2>.<id3>` query param. Stack survives refresh and back-button.
 */
export function useBoardStack(rootBoardId: string) {
  const [searchParams, setSearchParams] = useSearchParams()

  const descendantSegment = searchParams.get('board') ?? ''
  const descendants = useMemo(
    () => (descendantSegment ? descendantSegment.split('.').filter(Boolean) : []),
    [descendantSegment]
  )

  const stack = useMemo(() => [rootBoardId, ...descendants], [rootBoardId, descendants])

  const writeDescendants = useCallback(
    (next: string[]) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next.length === 0) {
            params.delete('board')
          } else {
            params.set('board', next.join('.'))
          }
          return params
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  const push = useCallback(
    (childBoardId: string) => {
      writeDescendants([...descendants, childBoardId])
    },
    [descendants, writeDescendants]
  )

  const pop = useCallback(() => {
    if (descendants.length === 0) return
    writeDescendants(descendants.slice(0, -1))
  }, [descendants, writeDescendants])

  return { stack, push, pop }
}
